import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { Autocomplete, FormControl, InputLabel, Select, MenuItem, Grid, Chip } from '@mui/material';
import { Search, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function AttivitaSvoltaDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [parentName, setParentName] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination & Sort & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('operation_date');
  const [order, setOrder] = useState('desc');

  // Filters
  const [users, setUsers] = useState([]);
  const [statiIniziali, setStatiIniziali] = useState([]);
  const [statiFinali, setStatiFinali] = useState([]);
  const [attivitaSvolte, setAttivitaSvolte] = useState([]);

  const [filterUser, setFilterUser] = useState(null);
  const [filterStatoIniziale, setFilterStatoIniziale] = useState('');
  const [filterStatoFinale, setFilterStatoFinale] = useState('');
  const [filterAttivitaSvolta, setFilterAttivitaSvolta] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState('');

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset page on search change
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchParent = useCallback(async () => {
    try {
      const res = await api.get(`/attivita/attivita-svolta/${id}`);
      setParentName(res.data?.name || 'Dettaglio');
      // Update local state for filter if lists are loaded
      setFilterAttivitaSvolta(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Errore caricamento attività');
    }
  }, [id]);

  const fetchOptions = useCallback(async () => {
    try {
      const [uRes, siRes, sfRes, asRes] = await Promise.all([
        api.get('/attivita/registro/users'),
        api.get('/attivita/stato-iniziale'),
        api.get('/attivita/stato-finale'),
        api.get('/attivita/attivita-svolta')
      ]);
      setUsers(uRes.data || []);
      setStatiIniziali(siRes.data || []);
      setStatiFinali(sfRes.data || []);
      setAttivitaSvolte(asRes.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: rowsPerPage,
        orderBy,
        order,
        search: debouncedSearch,
        user_id: filterUser?.id,
        stato_iniziale_id: filterStatoIniziale,
        stato_finale_id: filterStatoFinale,
        date_range: filterDateRange
      };
      const res = await api.get(`/attivita/attivita-svolta/${id}/details`, { params });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Errore caricamento dettagli');
    } finally {
      setLoading(false);
    }
  }, [id, page, rowsPerPage, orderBy, order, debouncedSearch, filterUser, filterStatoIniziale, filterStatoFinale, filterDateRange]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchParent();
  }, [fetchParent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Dettaglio Utilizzo: {parentName}
        </Typography>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.name || ''}
                value={filterUser}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(event, newValue) => {
                  setFilterUser(newValue);
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="Filtra per utente" size="small" placeholder="Cerca utente..." />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filtra per stato iniziale</InputLabel>
                <Select
                  value={filterStatoIniziale}
                  label="Filtra per stato iniziale"
                  onChange={(e) => {
                    setFilterStatoIniziale(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {statiIniziali.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={attivitaSvolte}
                getOptionLabel={(option) => option.name || ''}
                value={filterAttivitaSvolta}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(event, newValue) => {
                  if (newValue && newValue.id !== parseInt(id)) {
                     navigate(`/attivita/attivita-svolta/${newValue.id}`);
                  }
                  setFilterAttivitaSvolta(newValue);
                }}
                renderInput={(params) => <TextField {...params} label="Filtra per attività svolta" size="small" placeholder="Cerca attività..." />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filtra per stato finale</InputLabel>
                <Select
                  value={filterStatoFinale}
                  label="Filtra per stato finale"
                  onChange={(e) => {
                    setFilterStatoFinale(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {statiFinali.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Periodo</InputLabel>
                <Select
                  value={filterDateRange}
                  label="Periodo"
                  onChange={(e) => {
                    setFilterDateRange(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutto</MenuItem>
                  <MenuItem value="today">Oggi</MenuItem>
                  <MenuItem value="yesterday">Ieri</MenuItem>
                  <MenuItem value="7days">Ultimi 7 giorni</MenuItem>
                  <MenuItem value="30days">Ultimi 30 giorni</MenuItem>
                  <MenuItem value="this_month">Mese corrente</MenuItem>
                  <MenuItem value="last_month">Mese scorso</MenuItem>
                  <MenuItem value="this_year">Anno corrente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca (Cliente, Nota, Utente, Stati)..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={orderBy === 'operation_date' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'operation_date'}
                    direction={orderBy === 'operation_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('operation_date')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'user_name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'user_name'}
                    direction={orderBy === 'user_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_name')}
                  >
                    Utente
                  </TableSortLabel>
                </TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Istituto</TableCell>
                <TableCell sortDirection={orderBy === 'stato_iniziale_name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'stato_iniziale_name'}
                    direction={orderBy === 'stato_iniziale_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato_iniziale_name')}
                  >
                    Stato Iniziale
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'stato_finale_name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'stato_finale_name'}
                    direction={orderBy === 'stato_finale_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato_finale_name')}
                  >
                    Stato Finale
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'nota' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'nota'}
                    direction={orderBy === 'nota' ? order : 'asc'}
                    onClick={() => handleRequestSort('nota')}
                  >
                    Nota
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    Nessun risultato trovato
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.operation_date ? new Date(row.operation_date).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>{row.user_name || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.customer_name || '-'}</Typography>
                      {row.customer_email && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.customer_email}
                        </Typography>
                      )}
                      {row.customer_telephone && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.customer_telephone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.issuer_name || '-'}</Typography>
                      {row.issuer_direzione_generale && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.issuer_direzione_generale}
                        </Typography>
                      )}
                      {row.issuer_pec && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.issuer_pec}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.stato_iniziale_name ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: row.stato_iniziale_color || 'text.disabled',
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2">{row.stato_iniziale_name}</Typography>
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {row.stato_finale_name ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: row.stato_finale_color || 'text.disabled',
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2">{row.stato_finale_name}</Typography>
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{row.nota || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
